/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	Input,
	ViewChild,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	OnDestroy,
	OnInit,
} from '@angular/core';
import * as strings from '@touchplan/strings';
import { IButtonPanelEventData } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { ISchedulingDialogInput, ProjectSchedulingComponent } from '../project-scheduling.component';
import { MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { ExternalScheduleService } from 'src/app/services/external-schedule.service';
import {
	LineDisplayType,
	TextDisplayContainerComponent,
} from 'src/app/shared/ui/material/text-display-container/text-display-container.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatStatusIndicatorState } from 'src/app/shared/ui/material/mat-status-indicator-state';
import { UploadOutputFormat } from 'src/app/shared/ui/material/file-upload.component';

@Component({
	selector: 'app-external-schedule',
	templateUrl: './external-schedule.component.html',
	styleUrls: ['./external-schedule.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalScheduleComponent implements OnInit, OnDestroy {
	@ViewChild('displayContainer', { static: false }) private displayContainer: TextDisplayContainerComponent;
	@Input() public data: ISchedulingDialogInput;
	@Input() public hostDialogRef: MatDialogRef<ProjectSchedulingComponent>;

	public strings = strings;
	public UploadOutputFormat = UploadOutputFormat;
	public showExportPage = false;
	public exportCompleted = false;
	public exportCount = 0;
	public exportIndicatorState = MatStatusIndicatorState.InProgress;
	public readonly SCHEDULE_FILE_TYPES = ['.xls', '.xlsx'];
	public validationErrorDef = [
		{
			errorCode: 'dataFormatIncorrect',
			message: strings.EXTERNAL_SCHEDULE_FILE_UPLOAD_DEFAULT_ERROR_MESSAGE,
		},
	];
	public MatStatusIndicatorState = MatStatusIndicatorState;
	public fileContent: UntypedFormControl;

	private shutdownSubject$ = new Subject<void>();
	private selectedFile: File;

	constructor(
		private externalScheduleService: ExternalScheduleService,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	public ngOnInit(): void {
		this.fileContent = new UntypedFormControl(null, [
			Validators.required,
			//validate external schedule file content
			(control: AbstractControl): ValidationErrors | null => {
				const binaryString = control.value ? control.value[0] : null;
				let valid = !binaryString; // null selection is ok.
				if (binaryString) {
					// clean up in case user selected file several times
					this.externalScheduleService.destroy();
					const validation = this.externalScheduleService.validate(binaryString);
					valid = validation?.pass;
					if (!valid && validation.message) {
						this.validationErrorDef[0].message = validation.message;
					}
				}
				return valid ? null : ({ dataFormatIncorrect: true } as ValidationErrors);
			},
		]);
	}
	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		this.externalScheduleService.destroy();
	}

	public exportMilestones() {
		this.showExportPage = true;
		this.exportCompleted = false;
		this.exportIndicatorState = MatStatusIndicatorState.InProgress;

		this.externalScheduleService.process();
		// process result when complete
		this.externalScheduleService.processComplete$.pipe(takeUntil(this.shutdownSubject$)).subscribe((num) => {
			this.exportCompleted = true;
			this.exportCount = num;
			if (this.exportCount === 0) {
				this.exportIndicatorState = MatStatusIndicatorState.Failure;
			} else {
				this.exportIndicatorState = MatStatusIndicatorState.Success;
			}
			// need this for the UI to update
			this.changeDetectorRef.detectChanges();
		});

		this.externalScheduleService.exception$.pipe(takeUntil(this.shutdownSubject$)).subscribe((exception) => {
			if (this.displayContainer) {
				this.displayContainer.appendLine({ data: exception.message, type: LineDisplayType.Html });
			}
		});
		this.exportCompleted = true;
		this.exportCount = 0;
		this.exportIndicatorState = MatStatusIndicatorState.Failure;
	}

	public downloadOrCancel(): void {
		if (this.exportCompleted) {
			this.externalScheduleService.download(this.selectedFile.name);
		} else {
			//cancelling export
			this.externalScheduleService.destroy();
			this.showExportPage = false;
			this.exportCompleted = false;
			this.exportIndicatorState = MatStatusIndicatorState.Failure;
		}
	}

	public onClose() {
		this.hostDialogRef.close();
	}

	public handleFileSelection(files: File[]) {
		this.selectedFile = files[0];
	}
}
